import React from 'react';
import PropTypes from 'prop-types';
import { useDataModel } from '@thd-nucleus/data-sources';
import { visNavDataModel } from './dataModel';
import { VisualNavigationRenderer } from './VisualNavigationRenderer';
import { normalizeVisNavData } from '../utils/normalizeVisNavData';

const VisualNavigation = ({
  componentId = '',
  slotContentId: slotName = '',
  mediaCollectionIndex = 0,
  componentClass = '',
  componentPosition = 1,
  isInSection = false,
  lazyLoad = false,
  experienceHasSideNav = false,
  enableTheme = false,
  fontWeight = 'display',
  enableHomepageGap = false,
  enableB2CRadius = false,
  enableFullBleed = false,
  showInternalTitle = true
}) => {
  const { data, loading, error } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass: 'VisualNavigation'
    },
    skip: !!slotName
  });

  // New dataModel call - use cache-only and skip if no slotName
  const { data: slotData, loading: slotLoading, error: slotError } = useDataModel('slot', {
    fetchPolicy: 'cache-only',
    variables: {
      id: slotName
    },
    skip: !slotName
  });

  // Only check loading/error for queries that are not skipped
  if (componentId && (loading || error)) {
    return null;
  }

  if (slotName && (slotLoading || slotError)) {
    return null;
  }

  // Normalize data from different sources
  const normalizedData = normalizeVisNavData({
    slotData,
    componentData: data,
    mediaCollectionIndex
  });

  // Handle empty data
  if (!normalizedData?.list?.length) {
    return null;
  }

  const { component: transformedData, list: transformedList } = normalizedData;

  return (
    <VisualNavigationRenderer
      componentId={componentId || slotName}
      componentPosition={componentPosition}
      componentClass={componentClass}
      isInSection={isInSection}
      lazyLoad={lazyLoad}
      experienceHasSideNav={experienceHasSideNav}
      dataComponent={transformedData}
      visNavList={transformedList}
      displayName={VisualNavigation.displayName}
      enableTheme={enableTheme}
      fontWeight={fontWeight}
      enableHomepageGap={enableHomepageGap}
      enableB2CRadius={enableB2CRadius}
      enableFullBleed={enableFullBleed}
      showInternalTitle={showInternalTitle}
    />
  );
};
VisualNavigation.displayName = 'VisualNavigation';
VisualNavigation.propTypes = {
  componentId: PropTypes.string,
  slotContentId: PropTypes.string,
  mediaCollectionIndex: PropTypes.number,
  componentClass: PropTypes.string,
  componentPosition: PropTypes.number,
  isInSection: PropTypes.bool,
  lazyLoad: PropTypes.bool,
  experienceHasSideNav: PropTypes.bool,
  enableTheme: PropTypes.bool,
  fontWeight: PropTypes.string,
  enableHomepageGap: PropTypes.bool,
  enableB2CRadius: PropTypes.bool,
  enableFullBleed: PropTypes.bool,
  showInternalTitle: PropTypes.bool,
};

VisualNavigation.dataModel = visNavDataModel;

export { VisualNavigation };
